import { useEffect, useRef, useState } from "react";
 
import { Column } from "primereact/column";
 
import {
  ConfirmDialog,
  confirmDialog,
} from "primereact/confirmdialog";
 
import {
  DataTable,
  DataTableFilterEvent,
  DataTableFilterMetaData,
  DataTablePageEvent,
  DataTableSortEvent,
} from "primereact/datatable";
 
import { Dialog } from "primereact/dialog";
import { Toast } from "primereact/toast";
import { Calendar } from "primereact/calendar";
import { Button } from "primereact/button";
 
import Spinner from "presentation/components/Shared/Spinner";
 
import {
  useDeleteFormsInstanceMutation,
  useGetInstanceByFilter,
  useUpdateFormsInstanceMutation,
} from "presentation/hooks/use-query-formsinstance";
 
import {
  useFilterFormsInstanceStore,
} from "presentation/stores/use-filter-formsinstance-store";
 
import { FormInstance } from "domain/entities/form-instance";
 
import {
  FormInstanceInputFilter,
  FormSubmissionInputFilter,
} from "domain/models/nimberforms-input-filters";
 
import TypeFormsInstanceDropdown from "./TypeFormsInstanceDropdown";
 
import HeaderFormsInstanceGrid from "./HeaderFormsInstanceGrid";
 
import CreateInstance from "../FormEdit/CreateInstance";
 
import { exportFormResponsesToExcel } from "presentation/utils/exportUtils/forms-responses-export";
 
import { Tooltip } from "primereact/tooltip";

const FormsInstanceGrid: React.FC = () => {
  const toast = useRef<Toast>(null);
 
  const { filters, setFilters } =
    useFilterFormsInstanceStore();
 
  const deleteMutation =
    useDeleteFormsInstanceMutation();
 
  const [showExportModal, setShowExportModal] =
    useState(false);
 
  const updateInstance =
      useUpdateFormsInstanceMutation();
 
  const [
    selectedExportInstance,
    setSelectedExportInstance,
  ] = useState<FormInstance | null>(
    null
  );
 
  const [formMode, setFormMode] =
    useState<"create" | "edit">(
      "create"
    );
 
  const [showModal, setShowModal] =
    useState(false);
 
  const [
    selectedInstance,
    setSelectedInstance,
  ] = useState<FormInstance | null>(
    null
  );
 
  const [lazyParams, setLazyParams] =
    useState({
      sortBy: "createdAt",
      sortOrder: "desc",
      limit: 50,
      page: 1,
      filters: {},
    });
 
  const {
    data: forminstance,
    isLoading,
    isError,
    error,
    refetch,
  } = useGetInstanceByFilter({
    sortBy: lazyParams.sortBy,
    sortOrder:
      lazyParams.sortOrder,
    page: lazyParams.page,
    limit: lazyParams.limit,
    ...lazyParams.filters,
  } as FormInstanceInputFilter);
 
 
  const handleExport = async (
    instance: FormInstance,
    includeAnswers: boolean,
    isAnonymous: boolean
  ) => {
    try {
 
      const filter: FormSubmissionInputFilter =
        {
          instanceId: instance.id,
          isAnonymous: isAnonymous,
          includeAnswers,
        };
 
      const submissions = await exportFormResponsesToExcel(
        filter,
        instance.title
      );
      
      if(!submissions) {
        toast.current?.show({
          severity: "error",
          summary: "Erro",
          detail:
            "A instancia selecionada não tem respostas para exportar.",
          life: 3000,
        });
      } else {
        toast.current?.show({
          severity: "success",
          summary: "Sucesso",
          detail:
            "Exportação concluída.",
          life: 3000,
        });
      }
 
      setShowExportModal(false);
    } catch (err) {
      toast.current?.show({
        severity: "error",
        summary: "Erro ao exportar",
        detail:
          (err as Error)?.message ||
          "Não foi possível gerar o ficheiro Excel.",
        life: 3000,
      });
    }
  };
 
  useEffect(() => {
    setLazyParams((prev) => ({
      ...prev,
      filters: Object.fromEntries(
        Object.entries(filters)
          .map(([key, value]) => {
            if (
              "constraints" in value
            ) {
              return [
                key,
                value.constraints?.[0]
                  ?.value ?? "",
              ];
            }
 
            return [
              key,
              (
                value as DataTableFilterMetaData
              )?.value ?? "",
            ];
          })
          .filter(
            ([_, value]) =>
              value !== null &&
              value !== undefined &&
              value !== ""
          )
      ),
    }));
  }, [filters]);
 
  const handleDelete = async (
    id: string
  ) => {
    try {
      if (!id) {
        throw new Error(
          "ID inválido"
        );
      }
 
      await deleteMutation.mutateAsync(
        id
      );
 
      await refetch();
 
      toast.current?.show({
        severity: "success",
        summary: "Sucesso",
        detail:
          "Instância apagada com sucesso",
        life: 1500,
      });
    } catch (error: any) {
      toast.current?.show({
        severity: "error",
        summary: "Erro",
        detail:
          error?.message ||
          "Erro ao apagar instância",
        life: 1500,
      });
    }
  };
 
  const confirmAction = (
    message: string,
    acceptCallback: () => void
  ) => {
    confirmDialog({
      message,
      header: "Confirmação",
      icon:
        "pi pi-exclamation-triangle",
      acceptLabel: "Sim",
      rejectLabel: "Não",
      accept: acceptCallback,
    });
  };
 
  const showActionOptions = (
    rowData: FormInstance
  ) => {
    return (
      <div className="spacing-icons flex gap-2 justify-content-center align-items-center">
        <i
          className="pi pi-clipboard cursor-pointer"
          data-pr-tooltip="Copiar Link"
          onClick={() => {
            navigator.clipboard.writeText(`${window.location.origin}/respondToForm/${rowData.slug}`);
            toast.current?.show({
              severity: "success",
              summary: "Link copiado",
              detail: "O link foi copiado para a área de transferência."
            });
          }}
     
        />
 
        <i
          className={
            rowData.active
            ? "pi pi-pause cursor-pointer"
            : "pi pi-play cursor-pointer"
          }
          data-pr-tooltip={
            rowData.active
            ? "Desativar instância"
            : "Ativar instância"
          }
          onClick={() => {
            toggleActiveState(rowData);
          }}
     
        />
 
        <i
          className="pi pi-trash cursor-pointer"
          data-pr-tooltip="Apagar instância"
          onClick={() => {
            confirmAction(
              "Deseja apagar esta instância?",
              () =>
                handleDelete(
                  rowData.id || ""
                )
            );
          }}
        />
 
        <i
          className="pi pi-pencil cursor-pointer"
          data-pr-tooltip="Editar instância"
          onClick={() => {
            setSelectedInstance(
              rowData
            );
 
            setFormMode("edit");
 
            setShowModal(true);
          }}
        />
 
        <i
          className="pi pi-download cursor-pointer"
          data-pr-tooltip="Exportação de dados"
          onClick={() => {
            setSelectedExportInstance(
              rowData
            );
 
            setShowExportModal(true);
          }}
        />
    </div>);
  };
 
  const toggleActiveState = async (rowData: FormInstance) => {
    const instance: FormInstance = {
      ...rowData,
      active: !rowData.active,
    };
    await updateInstance.mutateAsync({
      id: rowData.id || "",
      instance: instance
    });
 
    await refetch();
 
    toast.current?.show({
      severity: "success",
      summary: "Sucesso",
      detail: `Instância ${instance.active ? "ativada" : "desativada"} com sucesso`,
      life: 1500,
    });
  }
 
  const tooltipStyle = {
    root: { style: { boxShadow: "none", border: "none", transition: "none", animation: "none", maxWidth: "120px", whiteSpace: "normal" as const, textAlign: "center" as const } },
    arrow: { style: { display: "none" } },
  };

  if (isLoading) {
    return <Spinner />;
  }
 
  if (isError) {
    toast.current?.show({
      severity: "error",
      summary: "Erro",
      detail:
        error instanceof Error
          ? error.message
          : "Ocorreu um erro inesperado",
      life: 1500,
    });
  }
 
  return (
    <div>
      <Tooltip target="[data-pr-tooltip]" position="top" pt={tooltipStyle} />
      <DataTable
        value={
          forminstance?.data ?? []
        }
        lazy
        paginator
        first={
          (lazyParams.page - 1) *
          lazyParams.limit
        }
        rows={lazyParams.limit}
        totalRecords={
          forminstance?.pagination
            ?.count || 0
        }
        loading={isLoading}
        onPage={(
          e: DataTablePageEvent
        ) =>
          setLazyParams({
            ...lazyParams,
            page: e.page
              ? e.page + 1
              : 1,
            limit: e.rows,
          })
        }
        sortField={
          lazyParams.sortBy
        }
        sortOrder={
          lazyParams.sortOrder ===
          "asc"
            ? 1
            : -1
        }
        onSort={(
          e: DataTableSortEvent
        ) =>
          setLazyParams({
            ...lazyParams,
            sortBy:
              e.sortField ||
              "createdAt",
            sortOrder:
              e.sortOrder === -1
                ? "desc"
                : "asc",
          })
        }
        onFilter={(
          e: DataTableFilterEvent
        ) => setFilters(e.filters)}
        filters={filters}
        size="small"
        resizableColumns
        columnResizeMode="expand"
        globalFilterFields={[
          "title",
          "isAnonymous",
          "active",
          "createdAt",
        ]}
        header={
          <HeaderFormsInstanceGrid />
        }
        rowsPerPageOptions={[
          2,
          10,
          25,
          50,
        ]}
        paginatorLeft
        dataKey="id"
      >
        <Column
          field="templateId.title"
          header="Template"
          />
        <Column
          field="title"
          header="Título"
          sortable
          filter
          showFilterMatchModes={
            false
          }
        />
 
        <Column
          field="isAnonymous"
          header="Anónima?"
          sortable
          filter
          body={(rowData) =>
            rowData.isAnonymous
              ? "Sim"
              : "Não"
          }
          filterElement={(options) => (
            <TypeFormsInstanceDropdown
              options={options}
              inputType="isAnonymous"
            />
          )}
          showFilterMatchModes={
            false
          }
        />
 
        <Column
          field="active"
          header="Estado"
          sortable
          filter
          body={(rowData) =>
            rowData.active
              ? "Ativo"
              : "Inativo"
          }
          filterElement={(options) => (
            <TypeFormsInstanceDropdown
              options={options}
              inputType="active"
            />
          )}
          showFilterMatchModes={
            false
          }
        />
 
        <Column
          field="createdAt"
          header="Data de Criação"
          sortable
          filter
          body={(rowData) =>
            new Date(
              rowData.createdAt
            )
              .toISOString()
              .split("T")[0]
              .split("-")
              .reverse()
              .join("-")
          }
          filterElement={(options) => (
            <Calendar
              value={
                options.value
                  ? new Date(
                      options.value
                    )
                  : null
              }
              onChange={(e) =>
                options.filterApplyCallback(
                  e.value
                    ? e.value.toISOString()
                    : null
                )
              }
              dateFormat="dd-mm-yy"
              showIcon
              className="w-full"
              placeholder="Selecionar data"
              showButtonBar
            />
          )}
          showFilterMatchModes={
            false
          }
        />
 
        <Column
          body={showActionOptions}
          header="Ações"
          align="center"
          alignHeader="center"
        />
      </DataTable>
 
      <Dialog
        visible={showModal}
        header={
          formMode === "edit"
            ? "Editar Instância"
            : "Criar Instância"
        }
        style={{ width: "50%" }}
        onHide={() => {
          setShowModal(false);
 
          setSelectedInstance(
            null
          );
 
          setFormMode("create");
        }}
      >
        <CreateInstance
          close={() => {
            setShowModal(false);
 
            setSelectedInstance(
              null
            );
 
            refetch();
          }}
          templateId={
            selectedInstance
              ?.templateId || ""
          }
          mode={formMode}
          initialData={
            selectedInstance
          }
        />
      </Dialog>
 
      <Dialog
        visible={showExportModal}
        header="Exportar respostas"
        style={{ width: "20rem" }}
        onHide={() => {
          setShowExportModal(false);
          setSelectedExportInstance(
            null
          );
        }}
      >
        <div className="flex flex-column gap-3">
          <Button
            label="Exportar respostas"
            icon="pi pi-file-excel"
            className="w-full"
            onClick={() => {
              if (
                selectedExportInstance
              ) {
                handleExport(
                  selectedExportInstance,
                  true,
                  true
                );
              }
            }}
          />
 
          {!selectedExportInstance?.isAnonymous && (
            <Button
              label="Exportar dados pessoais"
              icon="pi pi-user"
              outlined
              className="w-full"
              onClick={() => {
                if (
                  selectedExportInstance
                ) {
                  handleExport(
                    selectedExportInstance,
                    false,
                    false
                  );
                }
              }}
            />
          )}
 
          {!selectedExportInstance?.isAnonymous && (
            <Button
              label="Exportar respostas e dados pessoais"
              icon="pi pi-users"
              severity="success"
              className="w-full"
              onClick={() => {
                if (
                  selectedExportInstance
                ) {
                  handleExport(
                    selectedExportInstance,
                    true,
                    false
                  );
                }
              }}
            />
          )}
        </div>
      </Dialog>
          
      <ConfirmDialog />
      <Toast
        ref={toast}
        position="bottom-right"
      />
    </div>
  );
};
 
export default FormsInstanceGrid;
 